import React, { useEffect, useRef, useState } from "react";
import { agentApi } from "../../services/agentApi";
import {
  SESSION_STATUS_COLOR,
  sessionStatusLabel,
  sessionStepLabel,
} from "./sessionLabels";

const TERMINAL_STATUSES = ["ENDED", "HUMAN_HANDOFF", "SCHEDULED", "OPT_OUT"];

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function formatDayDivider(d) {
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return "Hoje";
  if (diffDays === 1) return "Ontem";
  return date.toLocaleDateString("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatMessageStamp(d) {
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sameCalendarDay(a, b) {
  if (!a || !b) return false;
  return startOfDay(new Date(a)) === startOfDay(new Date(b));
}

function StatusIcon({ status }) {
  if (!status) return null;
  if (status === "READ") return <span className="text-blue-500 text-xs">✓✓</span>;
  if (status === "DELIVERED") return <span className="text-gray-400 text-xs">✓✓</span>;
  if (status === "FAILED") return <span className="text-red-500 text-xs">⚠</span>;
  return <span className="text-gray-400 text-xs">✓</span>;
}

export default function ConversationThread({ sessionId, onSessionChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [busyAction, setBusyAction] = useState(false);
  const bottomRef = useRef(null);

  async function load() {
    if (!sessionId) return;
    setLoading(true);
    try {
      const r = await agentApi.sessions.get(sessionId);
      setData(r);
    } catch (err) {
      console.error("Failed to load thread", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setData(null);
    setDraft("");
    setActionError(null);
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [sessionId]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  async function handleSend(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setActionError(null);
    try {
      await agentApi.sessions.sendMessage(sessionId, text);
      setDraft("");
      await load();
    } catch (err) {
      const data = err?.response?.data;
      const msg = (typeof data?.error === "string" ? data.error : "") || err?.message || "";
      if (msg.includes("opted out")) setActionError("Contato descadastrado — não é possível enviar.");
      else if (msg.includes("session ended") || data?.error === "session ended")
        setActionError(
          data?.message ||
            "Conversa encerrada. Use Retomar conversa para iniciar um novo atendimento.",
        );
      else if (msg.includes("channel not connected")) setActionError("Canal WhatsApp desconectado.");
      else if (msg.includes("wasender send failed")) setActionError("Falha ao enviar pelo WhatsApp.");
      else setActionError(`Erro ao enviar: ${msg || "desconhecido"}`);
    } finally {
      setSending(false);
    }
  }

  async function handleReturnToAgent() {
    setBusyAction(true);
    setActionError(null);
    try {
      await agentApi.sessions.returnToAgent(sessionId);
      await load();
    } catch (err) {
      setActionError(err?.message || "Erro ao devolver ao agente");
    } finally {
      setBusyAction(false);
    }
  }

  async function handleEnd() {
    if (!confirm("Encerrar este atendimento? O histórico permanece e você poderá retomar depois.")) {
      return;
    }
    setBusyAction(true);
    setActionError(null);
    try {
      await agentApi.sessions.end(sessionId);
      await load();
    } catch (err) {
      const data = err?.response?.data;
      const msg = data?.error || err?.message || "";
      if (msg.includes("already ended")) setActionError("Esta conversa já está encerrada.");
      else setActionError(`Erro ao encerrar: ${msg || "desconhecido"}`);
    } finally {
      setBusyAction(false);
    }
  }

  async function handleResume() {
    setBusyAction(true);
    setActionError(null);
    try {
      const r = await agentApi.sessions.resume(sessionId);
      if (r?.sessionId && onSessionChange) onSessionChange(r.sessionId);
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("opted out")) setActionError("Contato descadastrado — não é possível retomar.");
      else if (msg.includes("active session")) setActionError("Já existe uma conversa ativa para este contato.");
      else setActionError(`Erro ao retomar: ${msg || "desconhecido"}`);
    } finally {
      setBusyAction(false);
    }
  }

  if (!sessionId) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 bg-gray-50">
        Selecione uma conversa
      </div>
    );
  }
  if (loading && !data) {
    return <div className="p-6 text-gray-500 bg-gray-50">Carregando...</div>;
  }
  if (!data) return null;

  const { session, messages } = data;
  const lead = session.lead;
  const humanControlled = !!session.assignedHumanId;
  const isOptOut = session.status === "OPT_OUT";
  const isTerminal = TERMINAL_STATUSES.includes(session.status);
  const canResume = isTerminal && !isOptOut;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 p-4 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-gray-900 font-semibold">{lead?.name}</div>
            <div className="text-gray-500 text-sm">{lead?.phone}</div>
          </div>
          <div className="flex items-start gap-2">
            {!isTerminal && !isOptOut && (
              <button
                onClick={handleEnd}
                disabled={busyAction}
                className="text-xs px-2.5 py-1.5 bg-white border border-red-200 hover:bg-red-50 disabled:opacity-50 text-red-600 rounded-md font-medium transition"
              >
                Encerrar atendimento
              </button>
            )}
            <div className="text-right">
              <div className="text-xs text-gray-400 mb-0.5">Status</div>
              <span
                className={`inline-block text-sm font-medium px-2.5 py-0.5 rounded-full ${SESSION_STATUS_COLOR[session.status] || "bg-blue-100 text-blue-700"}`}
              >
                {sessionStatusLabel(session.status)}
              </span>
              <div className="text-xs text-gray-400 mt-1">
                Etapa: {sessionStepLabel(session.currentStep)}
              </div>
              {session.status === "AWAITING_USER" && session.nextFollowUpAt && (
                <div className="text-xs text-gray-400 mt-1">
                  {session.collected?.followUpPhase === "closing"
                    ? "Encerramento com contato: "
                    : "Próximo toque: "}
                  {new Date(session.nextFollowUpAt).toLocaleString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {session.followUpCount > 0 && session.collected?.followUpPhase !== "closing"
                    ? ` (${session.followUpCount}ª retentativa)`
                    : ""}
                </div>
              )}
            </div>
          </div>
        </div>
        {lead?.processId && (
          <div className="text-xs text-gray-500 mt-2">Processo: {lead.processId}</div>
        )}
        {humanControlled && (
          <div className="mt-2 flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
            <span className="text-xs text-amber-800">
              Conversa assumida por atendente — o agente automático está pausado.
            </span>
            <button
              onClick={handleReturnToAgent}
              disabled={busyAction}
              className="text-xs px-2.5 py-1 bg-white border border-amber-300 hover:bg-amber-100 disabled:opacity-50 text-amber-800 rounded-md font-medium transition"
            >
              Devolver ao agente
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-100">
        {messages.map((m, index) => (
          <React.Fragment key={m.id}>
          {!sameCalendarDay(messages[index - 1]?.createdAt, m.createdAt) && (
            <div className="flex justify-center py-2">
              <span className="text-[11px] font-semibold text-gray-600 bg-white border border-gray-300 rounded-full px-3 py-1 shadow-sm">
                {formatDayDivider(m.createdAt)}
              </span>
            </div>
          )}
          <div
            className={`flex ${m.author === "LEAD" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[70%] px-3 py-2 rounded-lg text-sm shadow-sm ${
                m.author === "LEAD"
                  ? "bg-emerald-100 text-gray-900"
                  : m.author === "BOT"
                    ? "bg-white border border-gray-200 text-gray-900"
                    : "bg-blue-50 border border-blue-200 text-gray-900"
              }`}
            >
              <div className="text-xs text-gray-500 mb-1">
                {m.author === "BOT"
                  ? data?.agentName || "Marina"
                  : m.author === "LEAD"
                    ? lead?.name
                    : "Atendente"}
              </div>
              <div className="whitespace-pre-wrap">{m.text}</div>
              <div className="text-xs text-gray-500 text-right mt-1 flex items-center gap-1 justify-end">
                {formatMessageStamp(m.createdAt)}
                {m.direction === "OUTBOUND" && <StatusIcon status={m.status} />}
              </div>
            </div>
          </div>
          </React.Fragment>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-gray-200 bg-white p-3">
        {actionError && (
          <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
            {actionError}
          </div>
        )}

        {isOptOut ? (
          <div className="text-sm text-gray-500 text-center py-2">
            Contato descadastrado — envio de mensagens bloqueado.
          </div>
        ) : canResume ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-500">
              Conversa encerrada. Use Retomar para iniciar um novo atendimento
              (envio avulso nesta sessão não é permitido).
            </span>
            <button
              onClick={handleResume}
              disabled={busyAction}
              className="shrink-0 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm rounded-md font-medium transition"
            >
              {busyAction ? "Retomando..." : "Retomar conversa"}
            </button>
          </div>
        ) : null}

        {!isOptOut && !isTerminal && (
          <form onSubmit={handleSend} className="mt-2 flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              rows={1}
              placeholder="Escreva uma mensagem manual..."
              className="flex-1 resize-none px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:border-blue-500 placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              className="shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-md font-medium transition"
            >
              {sending ? "Enviando..." : "Enviar"}
            </button>
          </form>
        )}

        {!isOptOut && !isTerminal && !humanControlled && (
          <p className="text-xs text-gray-400 mt-1">
            Enviar uma mensagem assume a conversa e pausa o agente automático.
          </p>
        )}
      </div>
    </div>
  );
}
